import { msg, plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { useDeleteMediaManagerTemplateGroupMutation } from 'modules/manager/store/media-manager-api';
import { useCallback, useState } from 'react';
import { describeApiError } from 'shared/errors';
import Modal from 'ud-ui/components/modal';

/**
 * The group list and the championship «Медиа» tab refetch by the mutation's tags (IMGM-1515).
 *
 * A refusal keeps the window open and replaces its description with the reason (IMGM-1549):
 * before, 22412 «group is not empty» went only to the console and the window looked frozen.
 */
function useDeleteTemplateGroupModal() {
  const { t } = useLingui();
  const [deleteGroup, { isLoading }] = useDeleteMediaManagerTemplateGroupMutation();
  const [group, setGroup] = useState<MediaManagerTemplateGroup | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const open = useCallback((next: MediaManagerTemplateGroup) => {
    setGroup(next);
    setFailure(null);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
    setFailure(null);
  }, []);

  const onSubmit = useCallback(async () => {
    if (!group) return;
    try {
      // A non-empty group is refused with 22412 unless its templates go with it.
      await deleteGroup({ id: group.id, deleteTemplates: group.templatesCount > 0 }).unwrap();
      close();
    } catch (error) {
      setFailure(describeApiError(error, msg`Не удалось удалить группу`));
    }
  }, [group, deleteGroup, close]);

  const groupName = group?.name;
  const templatesCount = group?.templatesCount ?? 0;
  const templatesCountLabel = plural(templatesCount, {
    one: '# шаблон',
    few: '# шаблона',
    many: '# шаблонов',
    other: '# шаблонов',
  });
  const description =
    templatesCount > 0 ? t`В группе ${templatesCountLabel}. Они будут удалены вместе с группой` : t`Это действие необратимо.`;

  return {
    open,
    close,
    modal: (
      <Modal.Confirm
        show={isOpen}
        title={groupName ? t`Удалить группу «${groupName}»?` : t`Удалить группу?`}
        description={failure ?? description}
        isLoading={isLoading}
        onConfirm={onSubmit}
        onCancel={close}
      />
    ),
  };
}

export default useDeleteTemplateGroupModal;
